import React from "react";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_assistant_searchable_files } from "../../../constants";

// Shared reference-source picking for the AI generator modals (swipe deck +
// quiz). Fetches the company's searchable documents + folders, tracks file
// and folder selection, resolves selections to row objects for chip
// rendering, and expands selected folders into their file UUIDs (deduped)
// for the generation request. Mirrors the AI studio's loadReferenceFiles +
// expandedFileUuids so all three surfaces behave identically — including the
// stale-response sequence guard, so a fast company switch can't let an older
// in-flight response overwrite a newer one.
//
// `isOpen` drives the (re)fetch: pass the parent modal's open flag.
export function useReferenceSources(companyId, isOpen) {
  const [availableFiles, setAvailableFiles] = React.useState([]);
  const [availableFolders, setAvailableFolders] = React.useState([]);
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [selectedFolders, setSelectedFolders] = React.useState([]);
  const seqRef = React.useRef(0);

  const refresh = React.useCallback(async () => {
    if (!companyId) return;
    const seq = ++seqRef.current;
    // Clear BEFORE fetching: a reopen or company change must never leave the
    // previous scope's documents/folders selectable while the new request is
    // in flight. A stale selection would pass the generator's non-empty
    // source check, then be silently dropped by the backend's
    // company/deleted/searchable gates — so generation would run without the
    // documents the manager picked. Mirrors the picker's self-fetch clear.
    setAvailableFiles([]);
    setAvailableFolders([]);
    try {
      const res = await getRightworkAPI(API_get_assistant_searchable_files, {
        company_id: companyId,
      });
      if (seq !== seqRef.current) return; // superseded by a newer fetch
      setAvailableFiles(res.data?.redux?.payload?.files || []);
      setAvailableFolders(res.data?.redux?.payload?.folders || []);
    } catch (err) {
      if (seq !== seqRef.current) return;
      // Non-fatal: other source types (modules, sections, paste) still work.
      setAvailableFiles([]);
      setAvailableFolders([]);
    }
  }, [companyId]);

  React.useEffect(() => {
    if (isOpen) refresh();
  }, [isOpen, refresh]);

  const toggleFile = React.useCallback((uuid) => {
    setSelectedFiles((prev) =>
      prev.includes(uuid) ? prev.filter((x) => x !== uuid) : [...prev, uuid],
    );
  }, []);
  const toggleFolder = React.useCallback((uuid) => {
    setSelectedFolders((prev) =>
      prev.includes(uuid) ? prev.filter((x) => x !== uuid) : [...prev, uuid],
    );
  }, []);
  const reset = React.useCallback(() => {
    setSelectedFiles([]);
    setSelectedFolders([]);
  }, []);
  // Restore a persisted file selection (roleplay editor reload). Folder
  // selections aren't persisted anywhere — saves store expandedFileUuids —
  // so only files are seedable.
  const seedFiles = React.useCallback((uuids) => {
    setSelectedFiles(Array.isArray(uuids) ? uuids : []);
  }, []);

  const fileByUuid = React.useMemo(() => {
    const m = new Map();
    availableFiles.forEach((f) => m.set(f.uuid, f));
    return m;
  }, [availableFiles]);
  const folderByUuid = React.useMemo(() => {
    const m = new Map();
    availableFolders.forEach((f) => m.set(f.uuid, f));
    return m;
  }, [availableFolders]);

  // Selections resolved to full row objects (with name/source_url/file_count)
  // so chips render directly from the owned file/folder list — no separate
  // captured-label map.
  const selectedFileObjects = React.useMemo(
    () => selectedFiles.map((u) => fileByUuid.get(u)).filter(Boolean),
    [selectedFiles, fileByUuid],
  );
  const selectedFolderObjects = React.useMemo(
    () => selectedFolders.map((u) => folderByUuid.get(u)).filter(Boolean),
    [selectedFolders, folderByUuid],
  );

  // Selected files + every file inside a selected folder, deduped — what's
  // actually sent for generation.
  const expandedFileUuids = React.useMemo(() => {
    if (selectedFolders.length === 0) return selectedFiles;
    const folderSet = new Set(selectedFolders);
    const fromFolders = availableFiles
      .filter((f) => folderSet.has(f.folder_uuid))
      .map((f) => f.uuid);
    return Array.from(new Set([...selectedFiles, ...fromFolders]));
  }, [selectedFiles, selectedFolders, availableFiles]);

  return {
    availableFiles,
    availableFolders,
    selectedFiles,
    selectedFolders,
    selectedFileObjects,
    selectedFolderObjects,
    expandedFileUuids,
    toggleFile,
    toggleFolder,
    reset,
    seedFiles,
    refresh,
  };
}
